// @ts-nocheck
import { useCallback, useEffect, useRef, useState } from "react";
import { INotificationWebsocket } from "@/integration/application/interfaces/notification.interface.ts";
import { io, Socket } from "socket.io-client";

// Constants for WebSocket connection
const CONNECTION_TIMEOUT = 10000; // 10 seconds
const RECONNECT_INTERVAL = 3000; // 3 seconds
const MAX_RECONNECT_ATTEMPTS = 5;

// WebSocket connection states
enum ConnectionState {
  CONNECTING = "connecting",
  CONNECTED = "connected",
  DISCONNECTED = "disconnected",
  RECONNECTING = "reconnecting",
  FAILED = "failed",
}

/**
 * Enhanced WebSocket service that connects to the server with Socket.io
 * Refactored to use apiClient for any REST API calls
 */
export default function websocketService() {
  // Create a Socket.io connection to the server with timeout
  const createWebSocketConnection = (url: string): Socket => {
    const socketUrl = new URL(url);
    const baseUrl = `${socketUrl.protocol}//${socketUrl.host}`;
    const namespace = socketUrl.pathname;

    const socket = io(`${baseUrl}${namespace}`, {
      reconnectionAttempts: MAX_RECONNECT_ATTEMPTS,
      reconnectionDelay: RECONNECT_INTERVAL,
      timeout: CONNECTION_TIMEOUT,
      transports: ["websocket", "polling"],
      query: socketUrl.search
        ? Object.fromEntries(new URLSearchParams(socketUrl.search))
        : undefined,
    });

    return socket;
  };

  // Connect to the notification Socket.io endpoint
  const connectToNotifications = (
    userId: string,
    onMessage: (data: INotificationWebsocket) => void,
    onNotifications?: (data: any[]) => void, // Add handler for 'notifications' events
    onStatusChange?: (status: ConnectionState) => void
  ): {
    socket: Socket;
    disconnect: () => void;
  } => {
    // Get the API URL from environment variables
    const apiUrl = import.meta.env.VITE_SOME_API_URL;
    // No need to convert HTTP to WS as Socket.io handles this
    const fullUrl = `${apiUrl}/notifications`;

    let isManuallyDisconnected = false;
    let isConnecting = false;

    // Update connection status
    const updateStatus = (status: ConnectionState) => {
      if (onStatusChange) {
        onStatusChange(status);
      }
    };


    // Create initial connection with user ID in query params
    updateStatus(ConnectionState.CONNECTING);

    const socket = createWebSocketConnection(`${fullUrl}?userId=${userId}`);

    // Set up Socket.io event handlers
    socket.on("connect", () => {
      ("Socket.io connected");
      updateStatus(ConnectionState.CONNECTED);

      // Request platform notifications when connected
      socket.emit("getNotificationsPlatform", { userId });
    });

    socket.on("connect_error", (error) => {
      console.error("Socket.io connection error:", error);
      updateStatus(ConnectionState.DISCONNECTED);
    });

    socket.on("disconnect", (reason) => {
      `Socket.io disconnected. Reason: ${reason}`;
      updateStatus(ConnectionState.DISCONNECTED);

      // Handle different disconnect reasons
      if (!isManuallyDisconnected) {
        if (reason === "io server disconnect") {
          // The server has forcibly disconnected the socket
          ("Server forced disconnect. Manual reconnection needed.");
          socket.connect();
        } else if (reason === "transport close") {
          // The connection was closed (e.g., network issue)
          // Socket.io will automatically try to reconnect
          ("Transport closed. Socket.io will automatically reconnect.");
        } else if (reason === "transport error") {
          // There was a transport error
          // Socket.io will automatically try to reconnect
          ("Transport error. Socket.io will automatically reconnect.");
        } else if (reason === "ping timeout") {
          // The server did not respond to a ping in time
          // Socket.io will automatically try to reconnect
          ("Ping timeout. Socket.io will automatically reconnect.");
        } else if (reason === "client namespace disconnect") {
          // The client has been manually disconnected using socket.disconnect()
          ("Client namespace disconnect. No reconnection needed.");
        } else {
          // Unknown reason
          `Unknown disconnect reason: ${reason}. Socket.io will handle reconnection.`;
        }
      } else {
        ("Manual disconnect. No reconnection needed.");
      }
    });

    socket.on("reconnect_attempt", (attemptNumber) => {
      `Socket.io reconnect attempt ${attemptNumber} of ${MAX_RECONNECT_ATTEMPTS}`;
      updateStatus(ConnectionState.RECONNECTING);

      // If we've reached the maximum number of reconnect attempts
      if (attemptNumber >= MAX_RECONNECT_ATTEMPTS) {
        console.error(
          `Failed to reconnect after ${MAX_RECONNECT_ATTEMPTS} attempts. Stopping reconnection.`
        );
        updateStatus(ConnectionState.FAILED);

        // Prevent further reconnection attempts
        isManuallyDisconnected = true;
        socket.disconnect();

        // After a longer delay, reset the manual disconnect flag to allow future connection attempts
        // This prevents immediate reconnection but allows reconnection if the user refreshes or navigates
        setTimeout(() => {
          isManuallyDisconnected = false;
        }, CONNECTION_TIMEOUT * 2);
      }
    });

    socket.on("reconnect", () => {
      ("Socket.io reconnected");
      updateStatus(ConnectionState.CONNECTED);
    });

    socket.on("reconnect_failed", () => {
      console.error("Socket.io reconnection failed");
      updateStatus(ConnectionState.FAILED);
    });

    // Listen for notifications from the server
    socket.on("newNotification", (data: INotificationWebsocket) => {
      try {
        "CHEGADA_NEW_NOTIFICATION", data;
        onMessage(data);
      } catch (error) {
        console.error("Error handling notification:", error);
      }
    });

    // Listen for notifications array from getNotificationsPlatform
    socket.on("notifications", (data: any[]) => {
      try {
        "RECEIVED_PLATFORM_NOTIFICATIONS", data;
        if (onNotifications && Array.isArray(data)) {
          onNotifications(data);
        }
      } catch (error) {
        console.error("Error handling platform notifications:", error);
      }
    });

    // Define the online event handler
    const handleOnline = () => {
      if (!socket.connected && !isManuallyDisconnected) {
        ("Network is back online. Attempting to reconnect...");
        socket.connect();
      }
    };

    // Set up network status change listener
    window.addEventListener("online", handleOnline);

    // Function to manually disconnect and clean up
    const disconnect = () => {
      ("Manually disconnecting socket");

      // Set the flag to prevent automatic reconnection
      isManuallyDisconnected = true;

      // Remove all listeners to prevent memory leaks and unexpected behavior
      socket.removeAllListeners("connect");
      socket.removeAllListeners("connect_error");
      socket.removeAllListeners("disconnect");
      socket.removeAllListeners("reconnect_attempt");
      socket.removeAllListeners("reconnect");
      socket.removeAllListeners("reconnect_failed");
      socket.removeAllListeners("newNotification");
      socket.removeAllListeners("notifications");

      // Disconnect the socket
      socket.disconnect();

      // Remove the online event listener
      window.removeEventListener("online", handleOnline);
    };

    return { socket, disconnect };
  };

  // Custom hook to manage Socket.io connection lifecycle
  const useNotificationWebSocket = (
    userId: string | undefined,
    onMessage: (data: INotificationWebsocket) => void,
    onNotifications?: (data: any[]) => void
  ) => {
    const socketRef = useRef<Socket | null>(null);
    const disconnectRef = useRef<(() => void) | null>(null);
    const [connectionState, setConnectionState] = useState<ConnectionState>(
      ConnectionState.DISCONNECTED
    );
    const isConnectingRef = useRef(false);
    const userIdRef = useRef<string | undefined>(userId);

    // Update the userIdRef when userId changes
    useEffect(() => {
      userIdRef.current = userId;
    }, [userId]);

    // Store the callbacks in refs to avoid dependency changes
    const onMessageRef = useRef(onMessage);
    const onNotificationsRef = useRef(onNotifications);

    // Update the callback refs when the callbacks change
    useEffect(() => {
      onMessageRef.current = onMessage;
    }, [onMessage]);

    useEffect(() => {
      onNotificationsRef.current = onNotifications;
    }, [onNotifications]);

    // Handle connection status changes
    const handleStatusChange = useCallback((status: ConnectionState) => {
      setConnectionState(status);
    }, []);

    // Wrapper functions that use the refs
    const handleMessage = useCallback((data: INotificationWebsocket) => {
      if (onMessageRef.current) {
        onMessageRef.current(data);
      }
    }, []);

    const handleNotifications = useCallback((data: any[]) => {
      if (onNotificationsRef.current) {
        onNotificationsRef.current(data);
      }
    }, []);

    useEffect(() => {
      // Only connect if we have a userId and we're not already connecting
      if (!userId || isConnectingRef.current) return;

      // Mark as connecting to prevent multiple connections
      isConnectingRef.current = true;

      // Check if we already have a connection for this user
      if (
        socketRef.current &&
        socketRef.current.connected &&
        userIdRef.current === userId
      ) {
        "Already connected to WebSocket for user:", userId;
        isConnectingRef.current = false;
        return;
      }

      "Creating new WebSocket connection for user:", userId;

      // Create the Socket.io connection
      const { socket, disconnect } = connectToNotifications(
        userId,
        handleMessage,
        handleNotifications,
        handleStatusChange
      );

      socketRef.current = socket;
      disconnectRef.current = disconnect;
      isConnectingRef.current = false;

      // Clean up the Socket.io connection when the component unmounts
      return () => {
        "Cleaning up WebSocket connection for user:", userId;
        isConnectingRef.current = false;
        if (disconnectRef.current) {
          disconnectRef.current();
        }
      };
    }, [userId, handleStatusChange, handleMessage, handleNotifications]);

    return {
      socket: socketRef.current,
      connectionState,
    };
  };

  return {
    createWebSocketConnection,
    connectToNotifications,
    useNotificationWebSocket,
  };
}
